import { useEffect, useState } from "react";

import { apiService } from "../services";

/**
 * AS-1851: fetch the screener/interview metadata that backs the candidate-
 * facing header (top-left "Demo Company / Rocket Scientist" labels) on mount.
 *
 * Returns nulls until the request resolves; callers should fall back to the
 * legacy "Your Company" / "AI Interview" placeholders for the brief loading
 * window and for the failure case (the request rejects on network/4xx — we
 * log and stay on the fallback rather than tearing the page down).
 */
export function useLocationHeaderInfo(locationId: string): {
  companyName: string | null;
  jobTitle: string | null;
  locationName: string | null;
  // `isPending` is true until the fetch resolves (success OR error). Callers
  // gate analytics dispatch on `!isPending` rather than on the values being
  // non-null — otherwise legacy locations with null metadata would silently
  // suppress page and `Landed on Screener` analytics forever.
  isPending: boolean;
} {
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [jobTitle, setJobTitle] = useState<string | null>(null);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [isPending, setIsPending] = useState<boolean>(true);

  useEffect(() => {
    let isCancelled = false;
    setCompanyName(null);
    setJobTitle(null);
    setLocationName(null);
    setIsPending(true);

    apiService
      .getLocation(locationId)
      .then((location) => {
        if (isCancelled) return;
        setCompanyName(location.companyName ?? null);
        setJobTitle(location.jobTitle ?? null);
        setLocationName(location.name ?? null);
        setIsPending(false);
      })
      .catch((error) => {
        if (isCancelled) return;
        setCompanyName(null);
        setJobTitle(null);
        setLocationName(null);
        setIsPending(false);
        console.error("Failed to load interview location header info:", error);
      });
    return () => {
      isCancelled = true;
    };
  }, [locationId]);

  return { companyName, jobTitle, locationName, isPending };
}
